/**
 * screens/settings/billing.tsx — Billing & Invoices, ADMIN-only.
 * Invoice history and monthly statements, read from sk_billing. Paying, plan
 * changes and wallet top-ups live in the billing drawer (Plan & Usage), which is
 * the one place money is taken.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView, ActivityIndicator, Platform } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { usePlatform } from '../../context/platformContext';
import { useSession } from '../../context/sessionContext';
import { SettingsHeader, Panel, DataTable, StatusBadge } from '../../components/platform/primitives';
import { PButton, PSelect, PEmpty } from '../../components/platform/controls';
import Alert from '../../components/alert';
import { PermissionDenied } from '../shell/chrome';
import { FS, inr } from '../../utils/platformTokens';
import { listInvoices } from '../../services/platform/platformApi';
import { Platform_Org_Invoice_Pdf_Url, Billing_Statement_Url } from '../../api-endpoints/api-endpoints';
import { getApiErrorMessage } from '../../utils/errorHandler';
import type { Invoice } from '../../schema/platform/types';

const openFile = (url: string) => {
  if (Platform.OS === 'web') window.open(url, '_blank', 'noopener');
};

/** Last 12 calendar months as YYYY-MM, newest first. */
export function recentMonths(now = new Date(), count = 12): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  for (let i = 0; i < count; i += 1) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    const value = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    out.push({ value, label: d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' }) });
  }
  return out;
}

export const StatementDownload: React.FC<{ orgId: string }> = ({ orgId }) => {
  const months = useMemo(() => recentMonths(), []);
  const [month, setMonth] = useState(months[1]?.value ?? months[0].value);
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
      <PSelect value={month} onChange={setMonth} options={months} width={200} />
      <PButton icon="download" onPress={() => openFile(Billing_Statement_Url(orgId, month, 'pdf'))}>PDF</PButton>
      <PButton icon="download" onPress={() => openFile(Billing_Statement_Url(orgId, month, 'csv'))}>CSV</PButton>
    </View>
  );
};

const BillingScreen: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { role } = usePlatform();
  const { org } = useSession();
  const orgId = org?.id ?? '';
  const [invoices, setInvoices] = useState<Invoice[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(async (after?: string) => {
    if (!orgId) return;
    try {
      const page = await listInvoices(orgId, after);
      setInvoices((prev) => (after ? [...(prev ?? []), ...page.data] : page.data));
      setCursor(page.page.has_more ? page.page.next_cursor : null);
      setError(null);
    } catch (e) {
      setError(getApiErrorMessage(e, "Couldn't load invoices."));
      setInvoices((prev) => prev ?? []);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  if (role !== 'ADMIN') return <PermissionDenied />;
  const failed = (invoices ?? []).find((i) => i.status === 'failed' || i.status === 'past_due');

  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 1000, width: '100%', alignSelf: 'center' }}>
      <SettingsHeader title="Billing" sub="Tax invoices and monthly statements. Plans and top-ups are under Plan & Usage." />

      {!!failed && (
        <View style={{ marginBottom: 20 }}>
          <Alert tone="danger" title="A payment failed">
            Invoice {failed.invoice_number ?? failed.id} couldn't be charged. Open Plan & Usage to pay and avoid interruption.
          </Alert>
        </View>
      )}

      <Panel title="Statements" sub="Opening balance, grants, usage by product, transfers, adjustments and closing balance.">
        {orgId ? <StatementDownload orgId={orgId} /> : null}
      </Panel>

      <Panel title="Invoice history" bodyStyle={{ padding: 0 }}>
        {error && <View style={{ padding: 16 }}><Alert tone="danger">{error}</Alert></View>}
        {invoices === null ? (
          <View style={{ padding: 24 }}><ActivityIndicator color={c.accent} /></View>
        ) : (
          <DataTable<Invoice>
            minWidth={640}
            rows={invoices}
            keyExtractor={(i) => i.id}
            empty={<PEmpty icon="download" title="No invoices yet" />}
            columns={[
              { key: 'no', header: 'Invoice', width: 170, render: (i) => <Text style={{ fontFamily: theme.font.mono, color: c.ink, fontSize: FS.md }}>{i.invoice_number ?? i.id.slice(0, 8)}</Text> },
              { key: 'date', header: 'Issued', render: (i) => (i.issued_at ? new Date(i.issued_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—') },
              { key: 'tax', header: 'Tax', width: 110, align: 'right', render: (i) => <Text style={{ fontFamily: theme.font.mono, color: c.ink2 }}>{inr(i.tax.amount / 100)}</Text> },
              { key: 'total', header: 'Total', width: 120, align: 'right', render: (i) => <Text style={{ fontFamily: theme.font.mono, color: c.ink }}>{inr(i.total.amount / 100)}</Text> },
              { key: 'status', header: 'Status', width: 110, render: (i) => <StatusBadge status={i.status} /> },
              { key: 'pdf', header: '', width: 80, align: 'right', render: (i) => <PButton size="sm" kind="ghost" icon="download" onPress={() => openFile(Platform_Org_Invoice_Pdf_Url(orgId, i.id))}>PDF</PButton> },
            ]}
          />
        )}
        {cursor && (
          <View style={{ padding: 12, alignItems: 'center' }}>
            <PButton loading={loadingMore} onPress={async () => { setLoadingMore(true); await load(cursor); setLoadingMore(false); }}>Load more</PButton>
          </View>
        )}
      </Panel>
    </ScrollView>
  );
};

export default BillingScreen;
